/**
 * Send a league invitation email via Cloud Function
 * This keeps the Resend API key secure on the server side
 *
 * @param {Object} params - Email parameters
 * @param {string} params.toEmail - Recipient email address
 * @param {string} params.toName - Recipient name (if known)
 * @param {string} params.leagueName - Name of the league
 * @param {string} params.leagueId - League ID
 * @param {string} params.inviterName - Name of person sending invitation
 * @param {string} params.sport - Sport type
 * @param {string} params.invitationToken - Secure token for the invitation link
 * @param {string} params.role - Role being invited as (player, captain)
 * @param {string} params.teamName - Team name if being invited as captain
 */
export const sendLeagueInvitation = async ({
  toEmail,
  toName = 'there',
  leagueName,
  leagueId,
  inviterName,
  sport,
  invitationToken,
  role = 'player',
  teamName = null
}) => {
  try {
    // Call the secure cloud function
    const cloudFunctionUrl = 'https://us-central1-southern-list-468215-m7.cloudfunctions.net/sendLeagueInvitation';

    const response = await fetch(cloudFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        toEmail,
        toName,
        leagueName,
        leagueId,
        inviterName,
        sport,
        invitationToken,
        role,
        teamName,
        originUrl: window.location.origin
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || result.message || 'Failed to send invitation');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Error in sendLeagueInvitation:', error);
    throw error;
  }
};

/**
 * Send match notification email via Cloud Function
 *
 * @param {Object} params - Email parameters
 * @param {string} params.toEmail - Recipient email address
 * @param {string} params.toName - Recipient name (if known)
 * @param {string} params.leagueName - Name of the league
 * @param {string} params.leagueId - League ID
 * @param {string} params.opponent - Opponent name
 * @param {string} params.matchDate - Match date (formatted string)
 * @param {string} params.matchTime - Match time (optional)
 * @param {string} params.location - Match location (optional)
 */
export const sendMatchNotification = async ({
  toEmail,
  toName = 'there',
  leagueName,
  leagueId,
  opponent,
  matchDate,
  matchTime = null,
  location = null
}) => {
  try {
    // Call the secure cloud function
    const cloudFunctionUrl = 'https://us-central1-southern-list-468215-m7.cloudfunctions.net/sendMatchNotification';

    const response = await fetch(cloudFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        toEmail,
        toName,
        leagueName,
        leagueId,
        opponent,
        matchDate,
        matchTime,
        location,
        originUrl: window.location.origin
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || result.message || 'Failed to send match notification');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Error in sendMatchNotification:', error);
    throw error;
  }
};

export default {
  sendLeagueInvitation,
  sendMatchNotification
};
